import React, { useEffect, useState } from "react";
import { getDateDiffText, isValidDate, isValidEmail } from "../../utils/globalUtils";
import Button from "./Button";
import { AlertCircle, X } from "lucide-react";

const Input = React.forwardRef(({ children, label, type = "text", rows = 2, value, onChange, onClear, placeholder, autoFocus, required, disabled, visible, className, error, info, ...props }, ref) => {
    const [internalError, setInternalError] = useState(error);
    const [internalInfo, setInternalInfo] = useState(info);
    const [firstRender, setFirstRender] = useState(true);
    let labelClass = `text-xs text-primary-dark mb-1 gap-1 flex items-center`;
    let inputClass = `min-h-11 p-2 rounded w-full bg-background-light focus:outline-none text-primary-dark placeholder:text-gray-400 ${className || ''} ${disabled ? 'cursor-not-allowed' : ''}`;

    const validateError = (value) => {
        if (error) {
            setInternalError(error);
        } else if (type === "email" && value && !isValidEmail(value)) {
            setInternalError('Please enter a valid email address.');
        } else if (type === "date" && value && !isValidDate(new Date(value))) {
            setInternalError('Please enter a valid date.');
        } else {
            setInternalError(null);
        }
    }

    const validateInfo = (value) => {
        if (info) {
            setInternalInfo(info);
        // An EMPTY date field is empty, not invalid (EL-33). This branch used to answer a
        // date input nobody had touched yet with "Invalid date" - on the sign-up form that
        // put a complaint under Date of Birth before the user had reached it, and it did the
        // same on the profile, the alert dates and the admin plan dates. The hint below is
        // for a date that IS set (it reads back "35 years"), so with no value there is
        // nothing to say and saying nothing is the whole fix. A date that is set and
        // unparseable still errors, from validateError above.
        } else if (type === "date" && !value) {
            setInternalInfo(null);
        } else if (type === "date" && value) {
            setInternalInfo(getDateDiffText(new Date(value)));
        } else {
            setInternalInfo(null);
        }
    }

    useEffect(() => {
        validateInfo(value);
        if (firstRender) {
            setFirstRender(false);
            return;
        }
        validateError(value);
    }, [error, info, type, value]);

    const handleChange = (e) => {
        validateError(e.target.value);
        validateInfo(e.target.value);
        onChange(e);
    }

    const currentTheme = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark'
        ? 'dark'
        : 'light';
    const inputColorScheme = type === 'date' ? currentTheme : 'normal';

    return (
        <div className={`flex flex-col mb-3 w-full ${visible === false ? 'hidden' : ''}`}>
            {label && (
                <span className={labelClass}>
                    {label}
                    {required && <span className="text-red-500">*</span>}
                    <div className="ml-auto hidden">
                        {internalError && (
                            <span className="text-red-500 ml-auto flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {internalError}
                            </span>
                        )}
                        {internalInfo && !internalError && (
                            <span className="text-accent-main flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {internalInfo}
                            </span>
                        )}
                    </div>
                </span>
            )}
            <div className={`flex flex-row bg-background-light items-center border border-accent-main rounded ${internalError ? 'border-red-500' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                {type === "textarea" ? (
                    <textarea
                        ref={ref}
                        className={`${inputClass} resize-none`}
                        placeholder={placeholder}
                        value={value}
                        onChange={handleChange}
                        rows={rows}
                        {...(required && { required })}
                        {...(disabled && { disabled })}
                        {...(autoFocus && { autoFocus: true })}
                        style={{ colorScheme: inputColorScheme }}
                        {...props}
                    />
                ) : type === "file" ? (
                    <input
                        ref={ref}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.txt"
                        className={`${inputClass} cursor-pointer`}
                        placeholder={placeholder}
                        onChange={handleChange}
                        {...(required && { required })}
                        {...(disabled && { disabled })}
                        {...(autoFocus && { autoFocus: true })}
                        style={{ colorScheme: inputColorScheme }}
                        {...props}
                    />
                ) : type === "select" ? (
                    <select
                        ref={ref}
                        className={inputClass}
                        value={value}
                        onChange={handleChange}
                        {...(required && { required })}
                        {...(disabled && { disabled })}
                        {...(autoFocus && { autoFocus: true })}
                        style={{ colorScheme: inputColorScheme }}
                        {...props}
                    >
                        {React.Children.map(children, child => (
                            React.cloneElement(child, {
                                className: `text-primary-dark ${child.props.className || ''}`
                            })
                        ))}
                    </select>
                ) : (
                    <input
                        ref={ref}
                        type={type}
                        className={inputClass}
                        placeholder={placeholder}
                        value={value}
                        onChange={handleChange}
                        {...(required && { required })}
                        {...(disabled && { disabled })}
                        {...(autoFocus && { autoFocus: true })}
                        style={{ colorScheme: inputColorScheme }}
                        {...props}
                    />
                )}
                
                <Button
                    type="anchor"
                    size="small"
                    title="Clear"
                    className={`ml-auto p-0 min-w-[40px] hover:text-red-500 ${disabled ? 'hidden' : ''}`}
                    {...(disabled && { disabled })}
                    onClick={() => {
                        if (onClear) {
                            onClear();
                        } else {
                            onChange({ target: { files: [], value: '' } });
                        }
                    }}>
                    <X className="h-4 w-4" />
                </Button>
            </div>
            <div className="h-1 ml-auto">
                {internalError ? (
                    <span className="text-red-500 text-xs flex items-center gap-1 mt-1">
                        <AlertCircle className="w-3 h-3" />
                        {internalError}
                    </span>
                ) : internalInfo ? (
                    <span className="text-accent-main text-xs flex items-center gap-1 mt-1">
                        <AlertCircle className="w-3 h-3" />
                        {internalInfo}
                    </span>
                ) : null}
            </div>
        </div>
    );
});

export default Input;
